import { Suspense, lazy } from "react";
import { Route, Redirect, Switch } from "react-router-dom";
import { FloatingBugReportButton } from "app/components/modules/bugReport/BugReportModal";
/* ------------------------------ Lazy Imports ------------------------------ */
const isDevelopment = process.env.REACT_APP_ENV === "development";

const TakeoffPerformance = lazy(() => import("./routes/takeoff-performance"));
const PerformanceSubmissions = lazy(() =>
  import("./routes/performance-submissions")
);
const Main = lazy(() => import("./routes/main"));
const Library = lazy(() => import("./routes/library"));
const Settings = lazy(() => import("./routes/settings"));
const AdminGroupAndPermissions = lazy(() =>
  import("./routes/adminGroupAndPermissions")
);
const PendUsers = lazy(() => import("./routes/pend-users"));
const Users = lazy(() => import("./routes/users-management"));
const SignUp = lazy(() => import("./routes/signup-form"));
const UserEdit = lazy(() => import("./routes/users-management/UsersEdit"));

const Fleet = lazy(() => import("./routes/fleet"));
const FleetEdit = lazy(() => import("./routes/fleet/fleetEdit"));
const LegacyOpsPage = lazy(() => import("./routes/flights/index"));
const Reports = lazy(() => import("./routes/reports"));
const Forms = lazy(() => import("./routes/forms"));
const GeneralSetting = lazy(() => import("./routes/generalSettings"));
const Seats = lazy(() => import("./routes/seats"));
const FleetDocument = lazy(() => import("./routes/fleetDocument"));
const CrewPosition = lazy(() => import("./routes/crew-position"));
const CrewCertificates = lazy(() => import("./routes/crew-certificates"));
const EmailTemplates = lazy(() => import("./routes/email-templates"));
const EmailTemplateEdit = lazy(() =>
  import("./routes/email-templates/templateEdit")
);
const FlightEditing = lazy(() => import("./routes/FlightEditing"));
const DefaultFlight = lazy(() => import("./routes/company-flight"));
const Sched = lazy(() => import("./routes/sched"));
const CrewCurrency = lazy(() => import("./routes/crew-currency"));
const CrewRatings = lazy(() => import("./routes/crew-ratings"));
const FTLSettings = lazy(() => import("./routes/ftl-settings"));
const Ticketing = lazy(() => import("./routes/ticketing"));
const Messaging = lazy(() => import("./routes/messages"));
const Chat = lazy(() => import("./routes/ticketing/chat"));
const DutiesSetup = lazy(() => import("./routes/duties-setup"));
const CrewCalendar = lazy(() => import("./routes/crew-calendar"));
const AirlineModule = lazy(() => import("./routes/airlineModule"));
const LegacyOpsTimeline = lazy(() => import("./routes/flights/Timeline"));
const OpsCalendar = lazy(() => import("./routes/flights/Calendar"));
const LMS = lazy(() => import("./routes/lms"));
const ManageDashboards = lazy(() => import("./routes/manage-dashboards"));
const DashboardBuilder = lazy(() =>
  import("./routes/manage-dashboards/builder")
);
const DashboardBoard = lazy(() => import("./routes/manage-dashboards/board"));
const DesignSystemV2 = lazy(() => import("./routes/design-system-v2"));
const OpsTimeline = lazy(() => import("./routes/timeline-preview"));
const CrewCalendarPreview = lazy(() =>
  import("./routes/crew-calendar-preview")
);

/* -------------------------------------------------------------------------- */
// import FleetCreate from "./routes/fleet/fleetCreate";
// const { user, isAuthenticated } = useAuth0();
const Dashboard = ({ match }) => {
  return (
    <>
      <Suspense fallback={<></>}>
        <Switch>
          <Route exact path={`${match.url}`} component={Main} />
          <Route
            exact
            path={`${match.url}/performance/takeoff`}
            component={TakeoffPerformance}
          />
          <Route
            exact
            path={`${match.url}/performance/submissions`}
            component={PerformanceSubmissions}
          />
          <Redirect
            exact
            from={`${match.url}/takeoff-performance`}
            to={`${match.url}/performance/takeoff`}
          />
          <Route
            exact
            path={`${match.url}/manage-dashboards`}
            component={ManageDashboards}
          />
          <Route
            path={`${match.url}/manage-dashboards/:dashboardId`}
            component={DashboardBuilder}
          />
          <Route
            path={`${match.url}/boards/:dashboardId`}
            component={DashboardBoard}
          />
          <Route path={`${match.url}/library`} component={Library} />
          <Route path={`${match.url}/settings`} component={Settings} />
          <Route exact path={`${match.url}/flights`} component={OpsTimeline} />
          <Route
            path={`${match.url}/flights/calendar`}
            component={OpsCalendar}
          />
          {isDevelopment && (
            <Route
              path={`${match.url}/design-system-v2`}
              component={DesignSystemV2}
            />
          )}
          {isDevelopment && (
            <Route
              exact
              path={`${match.url}/timeline-preview`}
              component={LegacyOpsPage}
            />
          )}
          {isDevelopment && (
            <Route
              path={`${match.url}/timeline-preview/timeline`}
              component={LegacyOpsTimeline}
            />
          )}
          {isDevelopment && (
            <Route
              path={`${match.url}/timeline-preview/calendar`}
              component={OpsCalendar}
            />
          )}
          {isDevelopment && (
            <Route
              path={`${match.url}/crew-calendar-preview`}
              component={CrewCalendarPreview}
            />
          )}
          <Route exact path={`${match.url}/users/:id`} component={UserEdit} />
          <Route path={`${match.url}/users`} component={Users} />
          <Route
            path={`${match.url}/general-settings`}
            component={GeneralSetting}
          />
          <Route path={`${match.url}/seats`} component={Seats} />
          <Route path={`${match.url}/crew-position`} component={CrewPosition} />
          <Route
            path={`${match.url}/flight-editing`}
            component={FlightEditing}
          />
          <Route
            path={`${match.url}/flight-default`}
            component={DefaultFlight}
          />

          <Route
            path={`${match.url}/email-templates`}
            component={EmailTemplates}
          />
          <Route path={`${match.url}/messages`} component={Messaging} />
          <Route path={`${match.url}/ticketing`} component={Ticketing} exact />
          <Route path={`${match.url}/ticketing/chat`} component={Chat} exact />
          <Route path={`${match.url}/crew-currency`} component={CrewCurrency} />

          <Route
            path={`${match.url}/sub-template/:id`}
            component={EmailTemplateEdit}
          />
          <Route
            path={`${match.url}/crew-certificates`}
            component={CrewCertificates}
          />

          <Route path={`${match.url}/crew-ratings`} component={CrewRatings} />
          <Route path={`${match.url}/ftl-settings`} component={FTLSettings} />
          <Route path={`${match.url}/duties-setup`} component={DutiesSetup} />
          <Route path={`${match.url}/crew-calendar`} component={CrewCalendar} />
          {/* 
          <Route
            path={`${match.url}/privileges`}
            component={GroupAndPermissions}
          /> */}
          <Route
            path={`${match.url}/admin-privileges`}
            component={AdminGroupAndPermissions}
          />
          <Route path={`${match.url}/sched`} component={Sched} />
          <Route exact path={`${match.url}/pend-users`} component={PendUsers} />
          {/* <Route exact path={`${match.url}/users`} component={UserEdit} /> */}
          <Route path={`${match.url}/fleet`} component={Fleet} />
          <Route path={`${match.url}/fleets/:id`} component={FleetEdit} />
          <Route path={`${match.url}/reports`} component={Reports} />
          <Route path={`${match.url}/forms`} component={Forms} />
          <Route path={`${match.url}/lms/:tab?`} component={LMS} />

          <Route path={`${match.url}/fleets`} component={FleetEdit} />

          <Route path={`${match.url}/fleet-docs`} component={FleetDocument} />

          <Route path={`${match.url}/signup`} component={SignUp} />
          <Route
            path={`${match.url}/airlinemodule`}
            component={AirlineModule}
          />
          <Redirect exact from={`*`} to={`${match.url}`} />
        </Switch>
      </Suspense>
      <FloatingBugReportButton />
    </>
  );
};

export default Dashboard;
